import { Col, Input, Row } from "reactstrap";
import ProfileLayout from "../../../layouts/ProfileLayout";
import PropertyCard from "../../../components/Cards/PropertyCard";
import { useEffect, useState } from "react";
import { ReactComponent as EmptyIcon } from '../../../assets/images/icons/empty.svg';
import { API_URL } from "../../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function MyPropertiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('my_properties');
  const [filter, setFilter] = useState(new URLSearchParams(location.search).get('filter') || 'active');
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);

  const getProperties = (page = 1, append = false, ignoreCache = false) => {
    const baseCacheKey = `properties_data_${filter}`;
    const cachedData = sessionStorage.getItem(baseCacheKey);

    // Check if we have cached data and we're loading the first page
    if (!append && page === 1 && cachedData && !ignoreCache) {
      const parsed = JSON.parse(cachedData);
      setProperties(parsed.properties);
      setTotalProperties(parsed.totalProperties);
      setHasMorePages(parsed.hasMorePages);
      setCurrentPage(parsed.currentPage);
      setPropertiesLoading(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setPropertiesLoading(true);
    }

    fetch(`${API_URL}/user/properties?page=${page}&filter=${filter}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        let updatedProperties;

        if (append) {
          updatedProperties = [...properties, ...data.properties.data];
          setProperties(updatedProperties);
        } else {
          updatedProperties = data.properties.data;
          setProperties(updatedProperties);
        }

        // Update total properties
        setTotalProperties(data.properties.total);

        // Check if there are more pages
        const morePages = data.properties.current_page < data.properties.last_page;
        setHasMorePages(morePages);
        setCurrentPage(data.properties.current_page);

        // Always update cache with all loaded properties
        const cacheData = {
          properties: updatedProperties,
          totalProperties: data.properties.total,
          hasMorePages: morePages,
          currentPage: data.properties.current_page
        };
        sessionStorage.setItem(baseCacheKey, JSON.stringify(cacheData));
      }
      setPropertiesLoading(false);
      setLoadingMore(false);
    })
    .catch(error => {
      console.error('Error fetching properties:', error);
      setPropertiesLoading(false);
      setLoadingMore(false);
    });
  }

  const loadMore = () => {
    if (!loadingMore && hasMorePages) {
      getProperties(currentPage + 1, true);
    }
  }

  // Handle filter change
  useEffect(() => {
    setCurrentPage(1);
    getProperties(1, false, true);
  }, [filter]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMorePages) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Trigger when user is 200px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMorePages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    getProperties(1, false, new URLSearchParams(location.search).has('p'));
  }, [location.search]); 

  return(
    <ProfileLayout>
      <Row className="mb-3 justify-content-center">
        <Col lg="8">
          <Row className="align-items-center">
            <Col xs="8" md="10">
              <h4 className="mb-0">{t('title')} ({totalProperties})</h4>
            </Col>
            <Col xs="4" md="2" className="text-end">
              <Input size="sm" type="select" value={filter} onChange={(e) => {
                navigate(`?filter=${e.target.value}`);
                setFilter(e.target.value);
              }}>
                <option value="active">{t('status.active')}</option>
                <option value="archived">{t('status.archived')}</option>
              </Input>
            </Col>
          </Row>
        </Col>
      </Row>

      {propertiesLoading ?
        <div className="loader-container">
          <div className="loader"></div>
        </div>
      :
        <>
          <Row className="justify-content-center">
            {properties?.length > 0 ?
              properties.map((data, key) => {
                return(
                  <Col lg="8" key={key}>
                    <PropertyCard 
                      property={data} 
                      showViews 
                      refreshFunction={getProperties} 
                    />
                  </Col>
                );
              })
            :
              <Col md="12" className="text-center py-5">
                <EmptyIcon />

                <h6 className="mt-4 fw-normal text-muted">{t('empty')}</h6>
              </Col>
            }
          </Row>

          {loadingMore && (
            <Row className="justify-content-center mb-5">
              <Col md="12" className="text-center">
                <div className="loader"></div>
              </Col>
            </Row>
          )}
        </>
      }
    </ProfileLayout>
  );
}

export default MyPropertiesPage;